import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';
import { createCompactChartBase, resolvePointColor } from './chartConfigUtils';

interface GoldValuePoint {
    readonly date: string;
    readonly estimated?: boolean;
    readonly active24k: number;
    readonly passive24k: number;
    readonly expired24k: number;
}

interface GoldValueMultiChartProps {
    readonly data: readonly GoldValuePoint[];
    readonly loading?: boolean;
}

const goldValuePointFormatter: Highcharts.FormatterCallbackFunction<Highcharts.Point> =
    function () {
        const color = resolvePointColor(this.color, this.series?.color);
        const custom = this.options.custom as { estimatedLabel?: string } | undefined;
        return `<span style="color:${color}">●</span> ${this.series.name}: <b>${(this.y ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} g</b>${custom?.estimatedLabel ?? ''}<br/>`;
    };

function GoldValueMultiChart({ data, loading = false }: GoldValueMultiChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const base = createCompactChartBase(
        'area',
        data.map(d => d.date),
        t('statistics.value.gold24kGrams', 'Gold 24K (g)'),
    );
    const estimatedLabel = t(
        'statistics.value.estimatedLegacyPoint',
        ' (estimated legacy snapshot)',
    );
    const seriesData = (value: (point: GoldValuePoint) => number) =>
        data.map(point => ({
            y: value(point),
            custom: { estimatedLabel: point.estimated ? estimatedLabel : '' },
        }));

    const options: Highcharts.Options = {
        ...base,
        plotOptions: {
            area: {
                stacking: 'normal',
                marker: { enabled: false },
                lineWidth: 1,
            },
        },
        series: [
            {
                name: t('statistics.value.active', 'Active'),
                type: 'area',
                data: seriesData(point => Number(point.active24k ?? 0)),
                color: '#52c41a',
            },
            {
                name: t('statistics.value.passive', 'Passive'),
                type: 'area',
                data: seriesData(point => Number(point.passive24k ?? 0)),
                color: '#faad14',
            },
            {
                name: t('statistics.value.expired', 'Expired'),
                type: 'area',
                data: seriesData(point => Number(point.expired24k ?? 0)),
                color: '#ff4d4f',
            },
        ],
        legend: { enabled: true },
        credits: { enabled: false },
        tooltip: {
            shared: true,
            useHTML: true,
            backgroundColor: '#fff',
            borderColor: '#ccc',
            borderRadius: 4,
            padding: 8,
            pointFormatter: goldValuePointFormatter,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default GoldValueMultiChart;
